import { t, translateMessage } from "../i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  GripVertical,
  ImageOff,
  ImagePlus,
  Layers3,
  ListChecks,
  Package,
  Pencil,
  Plus,
  Trash2,
  X
} from "lucide-react";
import type { Category, Product, Setup } from "../types";
import { useBackHandler } from "../backNavigation";
import { encodePricedOptions, formatMoney, optionLabels, splitCounters, splitPricedOptions } from "../utils";

type Props = {
  setup: Setup;
  busy: boolean;
  onSaveSetup: (next: Setup) => void;
};

type ProductDraft = {
  id: number;
  name: string;
  print_name: string;
  category_id: number;
  aliases: string;
  withoutOptions: string[];
  extras: Array<{ label: string; price: string }>;
  variation_options: string;
  image_data: string;
  price: string;
  counters: Array<{ label: string; price: string }>;
};

type DragKind = "product" | "category";

type DragState = {
  kind: DragKind;
  id: number;
  active: boolean;
  pointerId: number;
  holdTimer: number | null;
  x: number;
  y: number;
};

function nextId(items: { id: number }[]): number {
  return items.reduce((highest, item) => Math.max(highest, item.id), 0) + 1;
}

function emptyProductDraft(categoryId: number, products: Product[]): ProductDraft {
  return {
    id: nextId(products),
    name: "",
    print_name: "",
    category_id: categoryId,
    aliases: "",
    withoutOptions: [],
    extras: [],
    variation_options: "",
    image_data: "",
    price: "",
    counters: []
  };
}

function toDraft(product: Product): ProductDraft {
  return {
    id: product.id,
    name: product.name,
    print_name: product.print_name || "",
    category_id: product.category_id,
    aliases: product.aliases || "",
    withoutOptions: optionLabels(product.without_options || ""),
    extras: splitPricedOptions(product.extras || "").map((option) => ({
      label: option.label,
      price: option.price ? String(option.price) : ""
    })),
    variation_options: product.variation_options || "",
    image_data: product.image_data || "",
    price: product.price ? String(product.price) : "",
    counters: splitCounters(product.counter_options).map((counter) => ({
      label: counter.label,
      price: counter.price ? String(counter.price) : ""
    }))
  };
}

const PRODUCT_IMAGE_WIDTH = 480;
const PRODUCT_IMAGE_HEIGHT = 320;
const PRODUCT_IMAGE_MAX_BYTES = 192 * 1024;
const PRODUCT_IMAGE_MAX_SOURCE_BYTES = 15 * 1024 * 1024;
const PRODUCT_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function encodedDataBytes(dataUrl: string): number {
  const encoded = dataUrl.split(",", 2)[1] ?? "";
  return Math.ceil(encoded.length * 3 / 4);
}

async function normalizeProductImage(file: File): Promise<string> {
  if (!PRODUCT_IMAGE_TYPES.has(file.type.toLowerCase())) {
    throw new Error(t("Επιτρέπονται εικόνες JPG, PNG ή WebP."));
  }
  if (file.size > PRODUCT_IMAGE_MAX_SOURCE_BYTES) {
    throw new Error(t("Η αρχική εικόνα ξεπερνά τα 15 MB."));
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(t("Η εικόνα δεν διαβάστηκε.")));
      image.src = objectUrl;
    });

    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;
    if (!sourceWidth || !sourceHeight) {
      throw new Error(t("Η εικόνα δεν έχει έγκυρες διαστάσεις."));
    }

    const targetRatio = PRODUCT_IMAGE_WIDTH / PRODUCT_IMAGE_HEIGHT;
    const sourceRatio = sourceWidth / sourceHeight;
    let sourceX = 0;
    let sourceY = 0;
    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    if (sourceRatio > targetRatio) {
      cropWidth = sourceHeight * targetRatio;
      sourceX = (sourceWidth - cropWidth) / 2;
    } else if (sourceRatio < targetRatio) {
      cropHeight = sourceWidth / targetRatio;
      sourceY = (sourceHeight - cropHeight) / 2;
    }

    const canvas = document.createElement("canvas");
    canvas.width = PRODUCT_IMAGE_WIDTH;
    canvas.height = PRODUCT_IMAGE_HEIGHT;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error(t("Η επεξεργασία εικόνας δεν υποστηρίζεται."));
    }
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      image,
      sourceX,
      sourceY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    for (const quality of [0.82, 0.74, 0.66, 0.58]) {
      const result = canvas.toDataURL("image/webp", quality);
      if (!result.startsWith("data:image/webp;base64,")) {
        throw new Error(t("Η συσκευή δεν υποστηρίζει μετατροπή εικόνας σε WebP."));
      }
      if (encodedDataBytes(result) <= PRODUCT_IMAGE_MAX_BYTES) {
        return result;
      }
    }
    throw new Error(t("Η συμπιεσμένη εικόνα παραμένει πολύ μεγάλη."));
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function moveId(order: number[], movingId: number, targetId: number): number[] {
  const from = order.indexOf(movingId);
  const to = order.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) {
    return order;
  }
  const next = order.slice();
  next.splice(from, 1);
  next.splice(to, 0, movingId);
  return next;
}

export function ProductsScreen({ setup, busy, onSaveSetup }: Props) {
  const sortedCategories = useMemo(
    () => setup.categories.slice().sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [setup.categories]
  );
  const [tab, setTab] = useState<"products" | "categories">("products");
  const [categoryId, setCategoryId] = useState<number>(sortedCategories[0]?.id ?? 0);
  const [productDraft, setProductDraft] = useState<ProductDraft | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageNote, setImageNote] = useState("");
  const [categoryDraft, setCategoryDraft] = useState<Category | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null);
  const [confirmMassDelete, setConfirmMassDelete] = useState(false);
  useBackHandler(Boolean(productDraft), () => setProductDraft(null));
  useBackHandler(Boolean(categoryDraft), () => setCategoryDraft(null));
  useBackHandler(Boolean(confirmDelete) || confirmMassDelete, () => {
    setConfirmDelete(null);
    setConfirmMassDelete(false);
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [productOrder, setProductOrder] = useState<number[] | null>(null);
  const [categoryOrder, setCategoryOrder] = useState<number[] | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const autoScrollRef = useRef<number | null>(null);
  const productOrderRef = useRef<number[] | null>(null);
  const categoryOrderRef = useRef<number[] | null>(null);

  useEffect(() => {
    if (!sortedCategories.some((category) => category.id === categoryId)) {
      setCategoryId(sortedCategories[0]?.id ?? 0);
    }
  }, [categoryId, sortedCategories]);

  useEffect(() => {
    setSelectedIds((current) => new Set(
      [...current].filter((id) => setup.products.some((product) => product.id === id && product.active))
    ));
  }, [setup.products]);

  const baseProducts = useMemo(
    () => setup.products
      .filter((product) => product.active && product.category_id === categoryId)
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [setup.products, categoryId]
  );
  const products = useMemo(() => {
    if (!productOrder) {
      return baseProducts;
    }
    const position = new Map(productOrder.map((id, index) => [id, index]));
    return baseProducts.slice().sort((a, b) =>
      (position.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (position.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [baseProducts, productOrder]);
  const categories = useMemo(() => {
    if (!categoryOrder) {
      return sortedCategories;
    }
    const position = new Map(categoryOrder.map((id, index) => [id, index]));
    return sortedCategories.slice().sort((a, b) =>
      (position.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (position.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [categoryOrder, sortedCategories]);

  const saveProduct = (draft: ProductDraft): void => {
    const name = draft.name.trim();
    if (!name) {
      return;
    }
    const price = Number.parseFloat(draft.price.replace(",", ".")) || 0;
    const counterOptions = draft.counters
      .map((counter) => {
        const label = counter.label.trim();
        if (!label) {
          return "";
        }
        const counterPrice = Number.parseFloat(counter.price.replace(",", ".")) || 0;
        return `${label}=${Math.max(0, counterPrice)}`;
      })
      .filter(Boolean)
      .join(";");
    const extraOptions = encodePricedOptions(draft.extras.map((option) => ({
      label: option.label,
      price: Number.parseFloat(option.price.replace(",", ".")) || 0
    })));
    const existing = setup.products.find((product) => product.id === draft.id);
    const updated: Product = {
      id: draft.id,
      name,
      print_name: draft.print_name.trim(),
      category_id: draft.category_id,
      aliases: draft.aliases.trim(),
      without_options: draft.withoutOptions
        .map((option) => option.trim().replace(/[,;\r\n]/g, " ").replace(/\s+/g, " "))
        .filter(Boolean)
        .join(", "),
      extras: extraOptions,
      counter_options: counterOptions,
      variation_options: draft.variation_options.trim(),
      image_data: draft.image_data,
      price: Math.max(0, price),
      sort_order: existing?.sort_order ?? setup.products.length,
      active: 1
    };
    const nextProducts = existing
      ? setup.products.map((product) => (product.id === updated.id ? updated : product))
      : [...setup.products, updated];
    onSaveSetup({ ...setup, products: nextProducts });
    setCategoryId(updated.category_id);
    setProductDraft(null);
  };

  const deactivateProducts = (ids: number[]): void => {
    onSaveSetup({
      ...setup,
      products: setup.products.map((product) =>
        ids.includes(product.id) ? { ...product, active: 0 } : product
      )
    });
    setSelectedIds(new Set());
    setConfirmDelete(null);
    setConfirmMassDelete(false);
  };

  const saveCategory = (draft: Category): void => {
    const name = draft.name.trim();
    if (!name) {
      return;
    }
    const existing = setup.categories.find((category) => category.id === draft.id);
    const updated: Category = { ...draft, name };
    const nextCategories = existing
      ? setup.categories.map((category) => (category.id === updated.id ? updated : category))
      : [...setup.categories, updated];
    onSaveSetup({ ...setup, categories: nextCategories });
    if (!existing) {
      setCategoryId(updated.id);
    }
    setCategoryDraft(null);
  };

  const deleteCategory = (category: Category): void => {
    if (setup.products.some((product) => product.active && product.category_id === category.id)) {
      return;
    }
    const nextCategories = setup.categories.filter((item) => item.id !== category.id);
    onSaveSetup({ ...setup, categories: nextCategories });
    setCategoryId(nextCategories[0]?.id ?? 0);
  };

  const commitProductOrder = (order: number[]): void => {
    const position = new Map(order.map((id, index) => [id, index]));
    onSaveSetup({
      ...setup,
      products: setup.products.map((product) =>
        product.category_id === categoryId && position.has(product.id)
          ? { ...product, sort_order: position.get(product.id) ?? product.sort_order }
          : product
      )
    });
    productOrderRef.current = null;
    setProductOrder(null);
  };

  const commitCategoryOrder = (order: number[]): void => {
    const position = new Map(order.map((id, index) => [id, index]));
    onSaveSetup({
      ...setup,
      categories: setup.categories.map((category) => ({
        ...category,
        sort_order: position.get(category.id) ?? category.sort_order
      }))
    });
    categoryOrderRef.current = null;
    setCategoryOrder(null);
  };

  const moveBy = (kind: DragKind, id: number, offset: number): void => {
    const order = (kind === "product" ? products : categories).map((item) => item.id);
    const index = order.indexOf(id);
    const target = Math.max(0, Math.min(order.length - 1, index + offset));
    if (index < 0 || index === target) {
      return;
    }
    const next = order.slice();
    next.splice(index, 1);
    next.splice(target, 0, id);
    if (kind === "product") {
      commitProductOrder(next);
    } else {
      commitCategoryOrder(next);
    }
  };

  const updateDragTarget = (): void => {
    const drag = dragRef.current;
    if (!drag?.active) {
      return;
    }
    const target = document.elementFromPoint(drag.x, drag.y)?.closest<HTMLElement>(
      `[data-${drag.kind}-order-id]`
    );
    const targetId = Number(target?.dataset[`${drag.kind}OrderId`]);
    if (!targetId || targetId === drag.id) {
      return;
    }
    if (drag.kind === "product") {
      const next = moveId(
        productOrderRef.current ?? products.map((item) => item.id),
        drag.id,
        targetId
      );
      productOrderRef.current = next;
      setProductOrder(next);
    } else {
      const next = moveId(
        categoryOrderRef.current ?? categories.map((item) => item.id),
        drag.id,
        targetId
      );
      categoryOrderRef.current = next;
      setCategoryOrder(next);
    }
  };

  const activateDrag = (kind: DragKind, id: number): void => {
    if (!dragRef.current || dragRef.current.kind !== kind || dragRef.current.id !== id) {
      return;
    }
    dragRef.current.active = true;
    setDraggingId(id);
    if (kind === "product") {
      const order = products.map((item) => item.id);
      productOrderRef.current = order;
      setProductOrder(order);
    } else {
      const order = categories.map((item) => item.id);
      categoryOrderRef.current = order;
      setCategoryOrder(order);
    }
    if (navigator.vibrate) {
      navigator.vibrate(15);
    }
  };

  const beginDrag = (
    kind: DragKind,
    id: number,
    event: React.PointerEvent<HTMLButtonElement>
  ): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const holdTimer = kind === "category"
      ? window.setTimeout(() => activateDrag(kind, id), 260)
      : null;
    dragRef.current = {
      kind,
      id,
      active: kind === "product",
      pointerId: event.pointerId,
      holdTimer,
      x: event.clientX,
      y: event.clientY
    };
    if (kind === "product") {
      activateDrag(kind, id);
    }
    if (autoScrollRef.current === null) {
      autoScrollRef.current = window.setInterval(() => {
        const drag = dragRef.current;
        if (!drag?.active) {
          return;
        }
        if (drag.y < 100) {
          window.scrollBy({ top: -18, behavior: "auto" });
          updateDragTarget();
        } else if (drag.y > window.innerHeight - 100) {
          window.scrollBy({ top: 18, behavior: "auto" });
          updateDragTarget();
        }
      }, 28);
    }
  };

  const moveDrag = (event: React.PointerEvent<HTMLButtonElement>): void => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    drag.x = event.clientX;
    drag.y = event.clientY;
    if (drag.active) {
      event.preventDefault();
      updateDragTarget();
    }
  };

  const endDrag = (): void => {
    const drag = dragRef.current;
    if (drag?.holdTimer !== null && drag?.holdTimer !== undefined) {
      window.clearTimeout(drag.holdTimer);
    }
    if (autoScrollRef.current !== null) {
      window.clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
    if (drag?.active) {
      if (drag.kind === "product") {
        commitProductOrder(productOrderRef.current ?? products.map((item) => item.id));
      } else {
        commitCategoryOrder(categoryOrderRef.current ?? categories.map((item) => item.id));
      }
    }
    dragRef.current = null;
    setDraggingId(null);
  };

  const selectedOnPage = products.filter((product) => selectedIds.has(product.id)).length;

  return (
    <section className="screen products-screen" aria-labelledby="products-heading">
      <div className="screen-heading-row">
        <div>
          <h1 id="products-heading">{t("Μενού")}</h1>
          <p>{setup.products.filter((product) => product.active).length}{t(" προϊόντα · ")}{categories.length}{t(" κατηγορίες")}</p>
        </div>
        <button
          type="button"
          className="primary-command"
          disabled={busy || (tab === "products" && !categories.length)}
          onClick={() => {
            if (tab === "products") {
              setImageNote("");
              setProductDraft(emptyProductDraft(categoryId, setup.products));
            } else {
              setCategoryDraft({ id: nextId(setup.categories), name: "", sort_order: setup.categories.length });
            }
          }}
        >
          <Plus />
          <span>{tab === "products" ? t("Νέο προϊόν") : t("Νέα κατηγορία")}</span>
        </button>
      </div>

      <div className="admin-tabs" role="tablist" aria-label={t("Διαχείριση μενού")}>
        <button type="button" className={tab === "products" ? "active" : ""} onClick={() => setTab("products")} role="tab" aria-selected={tab === "products"}>
          <Package />
          <span>{t("Προϊόντα")}</span>
        </button>
        <button type="button" className={tab === "categories" ? "active" : ""} onClick={() => setTab("categories")} role="tab" aria-selected={tab === "categories"}>
          <Layers3 />
          <span>{t("Κατηγορίες")}</span>
        </button>
      </div>

      {tab === "products" ? (
        <>
          <div className="category-strip" role="tablist" aria-label={t("Κατηγορίες")}>
            {categories.map((category) => (
              <button
                type="button"
                role="tab"
                aria-selected={category.id === categoryId}
                className={category.id === categoryId ? "active" : ""}
                onClick={() => {
                  setCategoryId(category.id);
                  setSelectedIds(new Set());
                }}
                key={category.id}
              >
                {category.name}
              </button>
            ))}
          </div>

          {products.length ? (
            <div className="bulk-action-bar">
              <label className="check-option compact-check">
                <input
                  type="checkbox"
                  checked={selectedOnPage === products.length}
                  onChange={(event) => setSelectedIds(event.target.checked
                    ? new Set(products.map((product) => product.id))
                    : new Set())}
                />
                <span>{selectedOnPage ? t("{0} επιλεγμένα", selectedOnPage) : t("Επιλογή όλων")}</span>
              </label>
              {selectedOnPage ? (
                <button type="button" className="secondary-command danger-command" onClick={() => setConfirmMassDelete(true)}>
                  <Trash2 />
                  <span>{t("Διαγραφή")}</span>
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="admin-list sortable-list">
            {products.map((product, index) => (
              <article
                className={`admin-row sortable-card ${draggingId === product.id ? "dragging" : ""}`}
                data-product-order-id={product.id}
                key={product.id}
              >
                <label className="row-select" aria-label={t("Επιλογή {0}", product.name)}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(product.id)}
                    onChange={(event) => setSelectedIds((current) => {
                      const next = new Set(current);
                      if (event.target.checked) next.add(product.id); else next.delete(product.id);
                      return next;
                    })}
                  />
                </label>
                <button
                  type="button"
                  className="icon-button drag-handle"
                  aria-label={t("Μετακίνηση {0}", product.name)}
                  title={t("Σύρετε για αλλαγή σειράς")}
                  onPointerDown={(event) => beginDrag("product", product.id, event)}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                >
                  <GripVertical />
                </button>
                <div className="admin-row-copy">
                  <strong>{product.name}</strong>
                  <span>{formatMoney(product.price)}</span>
                  {product.print_name && product.print_name !== product.name ? <small>{t("Χαρτί: ")}{product.print_name}</small> : null}
                  {product.variation_options ? <small>{t("Επιλογές: ")}{product.variation_options.replace(/[;\n]/g, ", ")}</small> : null}
                  {product.counter_options ? <small>{t("Μετρητές: ")}{product.counter_options.replace(/[;\n]/g, ", ")}</small> : null}
                </div>
                <div className="admin-row-actions compact-actions">
                  <button type="button" className="icon-button" disabled={index === 0} onClick={() => moveBy("product", product.id, -1)} aria-label={t("Πάνω")}><ArrowUp /></button>
                  <button type="button" className="icon-button" disabled={index === products.length - 1} onClick={() => moveBy("product", product.id, 1)} aria-label={t("Κάτω")}><ArrowDown /></button>
                  <button type="button" className="icon-button" onClick={() => { setImageNote(""); setProductDraft(toDraft(product)); }} aria-label={t("Επεξεργασία {0}", product.name)}><Pencil /></button>
                  <button type="button" className="icon-button danger-icon" onClick={() => setConfirmDelete(product)} aria-label={t("Διαγραφή {0}", product.name)}><Trash2 /></button>
                </div>
              </article>
            ))}
            {!products.length ? <div className="empty-state">{t("Δεν υπάρχουν προϊόντα σε αυτή την κατηγορία.")}</div> : null}
          </div>
        </>
      ) : (
        <div className="category-admin-grid sortable-list">
          {categories.map((category, index) => {
            const productCount = setup.products.filter((product) => product.active && product.category_id === category.id).length;
            return (
              <article
                className={`category-admin-card sortable-card ${draggingId === category.id ? "dragging" : ""}`}
                data-category-order-id={category.id}
                key={category.id}
              >
                <button
                  type="button"
                  className="icon-button drag-handle"
                  aria-label={t("Μετακίνηση {0}", category.name)}
                  title={t("Κρατήστε και σύρετε για αλλαγή σειράς")}
                  onPointerDown={(event) => beginDrag("category", category.id, event)}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                >
                  <GripVertical />
                </button>
                <div className="category-card-copy">
                  <strong>{category.name}</strong>
                  <span>{productCount}{t(" προϊόντα")}</span>
                </div>
                <div className="admin-row-actions compact-actions">
                  <button type="button" className="icon-button" disabled={index === 0} onClick={() => moveBy("category", category.id, -1)} aria-label={t("Πάνω")}><ArrowUp /></button>
                  <button type="button" className="icon-button" disabled={index === categories.length - 1} onClick={() => moveBy("category", category.id, 1)} aria-label={t("Κάτω")}><ArrowDown /></button>
                  <button type="button" className="icon-button" onClick={() => setCategoryDraft(category)} aria-label={t("Μετονομασία {0}", category.name)}><Pencil /></button>
                  <button
                    type="button"
                    className="icon-button danger-icon"
                    disabled={productCount > 0}
                    title={productCount > 0 ? t("Η κατηγορία έχει προϊόντα.") : t("Διαγραφή")}
                    onClick={() => deleteCategory(category)}
                    aria-label={t("Διαγραφή {0}", category.name)}
                  >
                    <Trash2 />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {productDraft ? (
        <div className="dialog-backdrop">
          <section className="details-dialog product-editor" role="dialog" aria-modal="true" aria-label={t("Στοιχεία προϊόντος")}>
            <header className="dialog-header">
              <div>
                <h2>{setup.products.some((product) => product.id === productDraft.id) ? t("Επεξεργασία προϊόντος") : t("Νέο προϊόν")}</h2>
                <p>{t("Όνομα εφαρμογής, εκτύπωσης και επιλογές παραγγελίας")}</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setProductDraft(null)} aria-label={t("Κλείσιμο")}><X /></button>
            </header>
            <div className="dialog-scroll form-grid">
              <label className="field"><span>{t("Όνομα στην εφαρμογή")}</span><input value={productDraft.name} onChange={(event) => setProductDraft({ ...productDraft, name: event.target.value })} autoFocus /></label>
              <label className="field"><span>{t("Όνομα στο χαρτί")}</span><input value={productDraft.print_name} placeholder={productDraft.name || t("Όνομα εκτύπωσης")} onChange={(event) => setProductDraft({ ...productDraft, print_name: event.target.value })} /></label>
              <label className="field"><span>{t("Κατηγορία")}</span><select value={productDraft.category_id} onChange={(event) => setProductDraft({ ...productDraft, category_id: Number.parseInt(event.target.value, 10) })}>{categories.map((category) => <option value={category.id} key={category.id}>{category.name}</option>)}</select></label>
              <label className="field"><span>{t("Τιμή")}</span><input inputMode="decimal" value={productDraft.price} onChange={(event) => setProductDraft({ ...productDraft, price: event.target.value })} /></label>
              <label className="field full-field"><span>{t("Επιλογές ποσότητας (χωρισμένες με ;)")}</span><input value={productDraft.variation_options} placeholder="0,5;1;1,5;2;2,5" onChange={(event) => setProductDraft({ ...productDraft, variation_options: event.target.value })} /></label>
              <section className="counter-editor full-field">
                <header>
                  <div><ListChecks /><span><strong>{t("Χωρίς")}</strong><small>{t("Επιλογές αφαίρεσης χωρίς χρέωση.")}</small></span></div>
                  <button type="button" className="secondary-command compact-command" onClick={() => setProductDraft({ ...productDraft, withoutOptions: [...productDraft.withoutOptions, ""] })}><Plus /><span>{t("Προσθήκη")}</span></button>
                </header>
                {productDraft.withoutOptions.map((option, index) => (
                  <div className="counter-editor-row option-name-row" key={index}>
                    <label className="field"><span>{t("Όνομα")}</span><input value={option} onChange={(event) => setProductDraft({ ...productDraft, withoutOptions: productDraft.withoutOptions.map((item, itemIndex) => itemIndex === index ? event.target.value : item) })} /></label>
                    <button type="button" className="icon-button danger-icon" onClick={() => setProductDraft({ ...productDraft, withoutOptions: productDraft.withoutOptions.filter((_, itemIndex) => itemIndex !== index) })} aria-label={t("Αφαίρεση επιλογής")}><Trash2 /></button>
                  </div>
                ))}
                {!productDraft.withoutOptions.length ? <p className="hint-text">{t("Δεν έχουν οριστεί επιλογές.")}</p> : null}
              </section>
              <section className="counter-editor full-field">
                <header>
                  <div><ListChecks /><span><strong>Extra</strong><small>{t("Η τιμή είναι προαιρετική. Κενό σημαίνει δωρεάν.")}</small></span></div>
                  <button type="button" className="secondary-command compact-command" onClick={() => setProductDraft({ ...productDraft, extras: [...productDraft.extras, { label: "", price: "" }] })}><Plus /><span>{t("Προσθήκη")}</span></button>
                </header>
                {productDraft.extras.map((option, index) => (
                  <div className="counter-editor-row" key={index}>
                    <label className="field"><span>{t("Όνομα")}</span><input value={option.label} onChange={(event) => setProductDraft({ ...productDraft, extras: productDraft.extras.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item) })} /></label>
                    <label className="field"><span>{t("Τιμή")}</span><input inputMode="decimal" value={option.price} placeholder="0,00" onChange={(event) => setProductDraft({ ...productDraft, extras: productDraft.extras.map((item, itemIndex) => itemIndex === index ? { ...item, price: event.target.value } : item) })} /></label>
                    <button type="button" className="icon-button danger-icon" onClick={() => setProductDraft({ ...productDraft, extras: productDraft.extras.filter((_, itemIndex) => itemIndex !== index) })} aria-label={t("Αφαίρεση extra")}><Trash2 /></button>
                  </div>
                ))}
                {!productDraft.extras.length ? <p className="hint-text">{t("Δεν έχουν οριστεί extra.")}</p> : null}
              </section>
              <section className="product-image-editor full-field">
                <header>
                  <div>
                    <ImagePlus />
                    <span>
                      <strong>{t("Εικόνα προϊόντος")}</strong>
                      <small>{t("JPG, PNG ή WebP έως 15 MB · αποθήκευση WebP 480 × 320, έως 192 KB")}</small>
                    </span>
                  </div>
                </header>
                {productDraft.image_data ? (
                  <img src={productDraft.image_data} alt={t("Προεπισκόπηση {0}", productDraft.name || t("προϊόντος"))} />
                ) : (
                  <div className="product-image-empty"><ImageOff /><span>{t("Χωρίς εικόνα")}</span></div>
                )}
                <div className="admin-row-actions product-image-actions">
                  <label className={`secondary-command compact-command ${imageBusy ? "disabled" : ""}`}>
                    <ImagePlus />
                    <span>{imageBusy ? t("Επεξεργασία…") : t("Επιλογή εικόνας")}</span>
                    <input
                      className="sr-only"
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      disabled={imageBusy}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        event.currentTarget.value = "";
                        if (!file) return;
                        setImageBusy(true);
                        setImageNote("");
                        void normalizeProductImage(file)
                          .then((imageData) => setProductDraft((current) => current ? { ...current, image_data: imageData } : current))
                          .catch((error) => setImageNote(error instanceof Error ? error.message : t("Η εικόνα δεν προστέθηκε.")))
                          .finally(() => setImageBusy(false));
                      }}
                    />
                  </label>
                  {productDraft.image_data ? (
                    <button type="button" className="secondary-command compact-command danger-command" disabled={imageBusy} onClick={() => setProductDraft({ ...productDraft, image_data: "" })}>
                      <Trash2 />
                      <span>{t("Αφαίρεση")}</span>
                    </button>
                  ) : null}
                </div>
                {imageNote ? <p className="hint-text error-text">{translateMessage(imageNote)}</p> : null}
              </section>
              <section className="counter-editor full-field">
                <header>
                  <div><ListChecks /><span><strong>{t("Μετρητές +/-")}</strong><small>{t("Κάθε μετρητής μπορεί να έχει διαφορετική τιμή.")}</small></span></div>
                  <button type="button" className="secondary-command compact-command" aria-label={t("Προσθήκη μετρητή")} onClick={() => setProductDraft({ ...productDraft, counters: [...productDraft.counters, { label: "", price: "" }] })}><Plus /><span>{t("Προσθήκη")}</span></button>
                </header>
                {productDraft.counters.map((counter, index) => (
                  <div className="counter-editor-row" key={index}>
                    <label className="field"><span>Label</span><input value={counter.label} onChange={(event) => setProductDraft({ ...productDraft, counters: productDraft.counters.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item) })} /></label>
                    <label className="field"><span>{t("Τιμή")}</span><input inputMode="decimal" value={counter.price} onChange={(event) => setProductDraft({ ...productDraft, counters: productDraft.counters.map((item, itemIndex) => itemIndex === index ? { ...item, price: event.target.value } : item) })} /></label>
                    <button type="button" className="icon-button danger-icon" onClick={() => setProductDraft({ ...productDraft, counters: productDraft.counters.filter((_, itemIndex) => itemIndex !== index) })} aria-label={t("Αφαίρεση μετρητή")}><Trash2 /></button>
                  </div>
                ))}
                {!productDraft.counters.length ? <p className="hint-text">{t("Δεν έχουν οριστεί μετρητές.")}</p> : null}
              </section>
              <label className="field full-field"><span>{t("Aliases αναζήτησης")}</span><input value={productDraft.aliases} onChange={(event) => setProductDraft({ ...productDraft, aliases: event.target.value })} /></label>
            </div>
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setProductDraft(null)} disabled={busy}><span>{t("Άκυρο")}</span></button>
              <button type="button" className="primary-command" disabled={busy || imageBusy || !productDraft.name.trim()} onClick={() => saveProduct(productDraft)}><Check /><span>{t("Αποθήκευση")}</span></button>
            </footer>
          </section>
        </div>
      ) : null}

      {categoryDraft ? (
        <div className="dialog-backdrop">
          <section className="details-dialog compact-dialog" role="dialog" aria-modal="true" aria-label={t("Κατηγορία")}>
            <header className="dialog-header"><div><h2>{setup.categories.some((category) => category.id === categoryDraft.id) ? t("Μετονομασία κατηγορίας") : t("Νέα κατηγορία")}</h2></div><button type="button" className="icon-button" onClick={() => setCategoryDraft(null)} aria-label={t("Κλείσιμο")}><X /></button></header>
            <div className="dialog-scroll"><label className="field"><span>{t("Όνομα κατηγορίας")}</span><input value={categoryDraft.name} onChange={(event) => setCategoryDraft({ ...categoryDraft, name: event.target.value })} autoFocus /></label></div>
            <footer className="dialog-actions"><button type="button" className="secondary-command" onClick={() => setCategoryDraft(null)} disabled={busy}><span>{t("Άκυρο")}</span></button><button type="button" className="primary-command" disabled={busy || !categoryDraft.name.trim()} onClick={() => saveCategory(categoryDraft)}><Check /><span>{t("Αποθήκευση")}</span></button></footer>
          </section>
        </div>
      ) : null}

      {confirmDelete || confirmMassDelete ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Διαγραφή προϊόντων")}>
            <header><h2>{t("Διαγραφή ")}{confirmMassDelete ? t("προϊόντων") : t("προϊόντος")}</h2><p>{confirmMassDelete ? t("Θα αφαιρεθούν {0} προϊόντα από το μενού.", selectedIds.size) : confirmDelete?.name}</p></header>
            <div className="dialog-actions"><button type="button" className="secondary-command" onClick={() => { setConfirmDelete(null); setConfirmMassDelete(false); }} disabled={busy}><span>{t("Άκυρο")}</span></button><button type="button" className="primary-command danger-command" disabled={busy} onClick={() => deactivateProducts(confirmMassDelete ? [...selectedIds] : confirmDelete ? [confirmDelete.id] : [])}><Trash2 /><span>{t("Διαγραφή")}</span></button></div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
